import { useLocation } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';

const DEMO_MODE_KEY = 'demo-mode';

/**
 * Returns true when the current route is under /demo/*.
 *
 * ROUTE-authoritative: demo mode is decided SOLELY by the pathname, never by
 * a stored flag. This is the single source of truth so demo/real data can
 * never bleed across the same shell — a real route (/vault, /dashboard,
 * /people) ALWAYS reads real data and a /demo/* route ALWAYS reads demo data.
 *
 *   const isDemo = useIsDemo();
 *   navigate(isDemo ? PATH_ROOT.demo.vault : PATH_ROOT.main.vault);
 *
 * Reactive to client-side navigation via react-router's useLocation — no
 * history patching, no event subscription needed. All consumers inherit the
 * route-authoritative behavior with no call-site changes.
 */
export function useIsDemo(): boolean {
  const { pathname } = useLocation();
  return pathname.startsWith(PATH_ROOT.demo.root);
}

/**
 * Legacy toggle. PRESERVED for callers (e.g. the demo welcome page) that still
 * write the flag, but it NO LONGER drives useIsDemo — the route is
 * authoritative. Kept so existing call sites compile and any code that reads
 * the localStorage flag directly stays in sync.
 */
export function setDemoMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(DEMO_MODE_KEY, 'true');
  } else {
    localStorage.removeItem(DEMO_MODE_KEY);
  }
  window.dispatchEvent(new StorageEvent('storage', { key: DEMO_MODE_KEY }));
}

/**
 * Picks the demo or prod path based on the current route context.
 *
 *   const paths = useDemoPaths();
 *   navigate(paths.calendar);  // → /demo/calendar or /calendar
 */
export function useDemoPaths() {
  const isDemo = useIsDemo();
  return isDemo ? PATH_ROOT.demo : PATH_ROOT.main;
}
